import { useContext, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Table,
  WithFetch,
  FetchData,
  WithTableLayout,
  TABLE_ACTION,
  TABLE_ACTION_STATE
} from '@qrios/frontend-library';

import { Dialog } from '../../../../../common/dialog';
import endpoints from '../../../../../../api/endpoints';
import TableFooterInfo from '../../../../../common/table/footerInfo';
import { PORTAL_PATH } from '../../../../../routes/paths';
import { formatToAmount } from '../../../../../../constants/helpers/utils';
import WithGlobalOverlay from '../../../../../common/with-global-overlay';
import SearchByOperationId from '../../../../../common/search-by-operation-id';
import { handleEndDateFilter } from '../../../../../../constants/helpers/dates';
import { fetchWithTokenRefresh } from '../../../../../../api/fetchWithTokenRefresh';

import { getQueryParams, handleRefreshOptions } from '../../../../../common/billing/apiUtils';
import { useSearchByOperationId } from '../../../../../common/search-by-operation-id/useSearchByOperationId';

import locales from './locales';
import { columns } from './columns';
import FilterForm from './filterForm';
import OperationDetails from './operation-details';
import { mapApiDataIntoTableFormat } from './dataTransformator';
import { AirtimeReportCreateForm } from '../reports/interfaces';
import { activeFiltersCountMapper } from './filters';
import { useShowOperationDetails } from './operation-details/useShowOperationDetails';
import { AirtimeBillingResponse, AirtimeBillingFilters, AirtimeBillingTableRowActions } from './interfaces';
import { billingTableEmptyMsg } from '../../../../../../constants/utils';
import { FILTERS_PATH, FiltersContext } from '../../../../../common/filters/filtersContext';

const apiOptions = {
  totalCost: true
};

const Billing = () => {
  const history = useHistory();

  const { filtersValues, handleUpdateFilters } = useContext(FiltersContext);
  const [activeFilters, setActiveFilters] = useState(filtersValues.airtime.billing);
  const initFetchOptions = getQueryParams({ filters: activeFilters, apiOptions });

  const {
    openDetailsDialog,
    handleOpenDetailsDialog,
    handleCloseDetailsDialog,
    selectedOperationDetails,
    waitingForDialogOpening
  } = useShowOperationDetails();

  const {
    searchByOperationIdState,
    handleSearchByOperationId,
    isSearching,
    openSearchOperationDialog,
    handleSearchOperationOpenDialog,
    handleSearchOperationCloseDialog
  } = useSearchByOperationId({ searchEndpoint: operationId => endpoints.airtimeAndDataOperations(operationId) });

  const handleGenerateReport = () => {
    if (activeFilters) {
      const { from, to, operation_id, status, acquirer_msisdn, sku, operator } = activeFilters;
      const reportFilters: AirtimeReportCreateForm = {
        name: '',
        from,
        to: handleEndDateFilter(to),
        operators: operator,
        operationIds: operation_id,
        statuses: status,
        skus: sku,
        acquirerMsisdns: acquirer_msisdn
      };
      history.push(PORTAL_PATH.AIRTIME_AND_DATA_REPORTS, { filters: reportFilters });
    }
  };

  const tableRowActions: AirtimeBillingTableRowActions = {
    openDetails: handleOpenDetailsDialog
  };

  return (
    <>
      <WithGlobalOverlay loading={waitingForDialogOpening} />
      <WithFetch
        fetchProvider={fetchWithTokenRefresh}
        pathname={endpoints.airtimeAndDataBilling}
        options={initFetchOptions}
        withLoader={false}
        withError={false}
      >
        {({ data, refresh, loading, error, previousOptions }: FetchData<AirtimeBillingResponse | undefined>) => (
          <WithTableLayout>
            <Table
              dataGridProps={{
                rows: !data?.items || loading ? [] : mapApiDataIntoTableFormat(data.items, tableRowActions),
                columns,
                loading,
                error: {
                  message: error,
                  refresh: () => refresh(previousOptions)
                },
                emptyMsg: billingTableEmptyMsg({
                  view: 'Top-up & Data',
                  operationType: 'operations',
                  from: activeFilters.from,
                  to: handleEndDateFilter(activeFilters.to),
                  hasActiveFilters: activeFiltersCountMapper(activeFilters) > 0
                })
              }}
              footerProps={{
                tableInfo: (
                  <TableFooterInfo
                    label={locales.footer.label}
                    tooltip={locales.footer.tooltip}
                    value={data ? formatToAmount(data.totalCost.value) : '-'}
                  />
                ),
                reportProps: {
                  disabled: loading,
                  name: TABLE_ACTION.REPORT,
                  state: TABLE_ACTION_STATE.ACTIVE,
                  handler: handleGenerateReport
                },
                searchProps: {
                  disabled: loading,
                  name: TABLE_ACTION.SEARCH,
                  state: TABLE_ACTION_STATE.ACTIVE,
                  handler: handleSearchOperationOpenDialog
                },
                refreshProps: {
                  disabled: loading,
                  handler: () => {
                    refresh(handleRefreshOptions(previousOptions, activeFilters.to));
                  }
                },
                filtersProps: {
                  disabled: loading,
                  filters: activeFilters,
                  activeFiltersCount: activeFiltersCountMapper(activeFilters),
                  form: FilterForm,
                  handleSubmit: (updatedFilters: AirtimeBillingFilters) => {
                    const queryParams = getQueryParams({ filters: updatedFilters, apiOptions });

                    handleUpdateFilters({
                      filtersToUpdatePath: FILTERS_PATH.AIRTIME_BILLING_PATH,
                      newFilters: updatedFilters
                    });
                    refresh(queryParams);
                    setActiveFilters(updatedFilters);
                  }
                },
                paginationProps: {
                  prev: {
                    disabled: loading || !data?.pagingContext.prevPageMarker,
                    handler: () => {
                      if (data?.pagingContext.prevPageMarker) {
                        const queryParams = getQueryParams({
                          filters: activeFilters,
                          apiOptions,
                          paginationOptions: { prev_marker: data.pagingContext.prevPageMarker }
                        });

                        refresh(queryParams);
                      }
                    }
                  },
                  next: {
                    disabled: loading || !data?.pagingContext.nextPageMarker,
                    handler: () => {
                      if (data?.pagingContext.nextPageMarker) {
                        const queryParams = getQueryParams({
                          filters: activeFilters,
                          apiOptions,
                          paginationOptions: { next_marker: data.pagingContext.nextPageMarker }
                        });

                        refresh(queryParams);
                      }
                    }
                  }
                }
              }}
            />
          </WithTableLayout>
        )}
      </WithFetch>
      {selectedOperationDetails && (
        <Dialog handleClose={handleCloseDetailsDialog} open={openDetailsDialog} title={locales.dialog.details.title}>
          <OperationDetails operation={selectedOperationDetails} />
        </Dialog>
      )}
      <Dialog
        open={openSearchOperationDialog}
        title={locales.dialog.search.title}
        handleClose={handleSearchOperationCloseDialog}
      >
        <SearchByOperationId
          state={searchByOperationIdState}
          Results={OperationDetails}
          handleSearch={handleSearchByOperationId}
          isSearching={isSearching}
        />
      </Dialog>
    </>
  );
};

export default Billing;
